import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import ProgressRing from '../components/ProgressRing';
import SubjectsRing, { SubjectRingItem } from '../components/SubjectsRing';
import ActivityFeed from '../components/ActivityFeed';

interface QuizResult {
  percentage: number;
}

interface SubjectResult {
  subjectId: number | null;
  subjectName: string;
  percentage: number | null;
}

/** Group flat subject-quiz results into one ring item per subject (avg % + attempts). */
const groupSubjects = (results: SubjectResult[]): SubjectRingItem[] => {
  const map = new Map<string, { name: string; sum: number; count: number }>();
  results.forEach((r) => {
    if (r.percentage == null) return;
    const key = r.subjectName || String(r.subjectId);
    const e = map.get(key) || { name: r.subjectName || 'Subject', sum: 0, count: 0 };
    e.sum += r.percentage;
    e.count += 1;
    map.set(key, e);
  });
  return Array.from(map.values()).map((e) => ({ name: e.name, percent: e.sum / e.count, attempts: e.count }));
};

interface HomeworkItem {
  status: 'PENDING' | 'DONE';
}

type HomeTab = 'progress' | 'activities';

const KidHome: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useAuthStore();

  const [tab, setTab] = useState<HomeTab>('progress');
  const [mathPct, setMathPct] = useState<number | null>(null);
  const [readingPct, setReadingPct] = useState<number | null>(null);
  const [homeworkPct, setHomeworkPct] = useState<number | null>(null);
  const [subjectItems, setSubjectItems] = useState<SubjectRingItem[]>([]);

  const average = (results: QuizResult[]): number | null =>
    results.length === 0
      ? null
      : results.reduce((sum, r) => sum + (r.percentage || 0), 0) / results.length;

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const [mathRes, readingRes, hwRes, subjRes] = await Promise.all([
          api.get(`/api/kids/${userId}/math/results`),
          api.get(`/api/kids/${userId}/reading/results`),
          api.get(`/api/kids/${userId}/homework`),
          api.get(`/api/kids/${userId}/subjects/results`),
        ]);
        setMathPct(average(mathRes.data));
        setReadingPct(average(readingRes.data));
        const hw: HomeworkItem[] = hwRes.data;
        setHomeworkPct(
          hw.length === 0 ? null : (hw.filter((h) => h.status === 'DONE').length / hw.length) * 100
        );
        setSubjectItems(groupSubjects(subjRes.data));
      } catch {}
    };
    fetchProgress();
  }, [userId]);

  return (
    <div className="space-y-5">
      {/* Tab switcher */}
      <div className="flex gap-2 bg-white/70 backdrop-blur rounded-full p-1 shadow-sm">
        <button
          onClick={() => setTab('progress')}
          className={`kt-tab flex-1 flex items-center justify-center gap-1.5 ${
            tab === 'progress' ? 'bg-brand-yellow text-gray-900 shadow' : 'text-gray-500'
          }`}
        >
          📊 KidsTrack Progress
        </button>
        <button
          onClick={() => setTab('activities')}
          className={`kt-tab flex-1 flex items-center justify-center gap-1.5 ${
            tab === 'activities' ? 'bg-brand-blue text-white shadow' : 'text-gray-500'
          }`}
        >
          ✨ Activities
        </button>
      </div>

      {tab === 'progress' ? (
        <section className="kt-card p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-display font-bold text-gray-800">My Progress</h2>
            <button
              onClick={() => navigate('/kid/history')}
              className="text-sm text-brand-blue hover:underline flex items-center gap-1 font-bold"
            >
              📊 My Results
            </button>
          </div>
          <div className="grid grid-cols-2 gap-5 justify-items-center">
            <ProgressRing percent={mathPct} label="Math Explorer" icon="🧮" gradient={['#C084FC', '#7C3AED']} />
            <ProgressRing percent={readingPct} label="Reading Journey" icon="📖" gradient={['#FDBA74', '#F97316']} />
            <ProgressRing percent={homeworkPct} label="Homework" icon="📚" gradient={['#7DD3FC', '#2563EB']} />
            <SubjectsRing subjects={subjectItems} showLegend />
          </div>
        </section>
      ) : (
        <ActivityFeed kidId={userId} />
      )}
    </div>
  );
};

export default KidHome;
